import { Button, DatePicker, Table, Tag, Row, Col, message } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import dayjs from "dayjs";
import { token } from "../../../constants/Constants";
import { selectBaseUrl, selectProdTestUrl } from "../../../redux/slices/envSlice";
import { get_tat_report } from "../../../endpoints/API";

const TatReport = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const { RangePicker } = DatePicker;
  const [data, setData] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [averages, setAverages] = useState({});

  React.useEffect(() => {
    fetchReportData(selectedDates);
  }, []);

  const fetchReportData = async (dates) => {
    try {
      const response = await get_tat_report(dates?.[0], dates?.[1]);
      if (!response.statusText == "OK") throw new Error("Network response was not ok");
      const result = response?.data;
      setData(result.data || []);
      setAverages(result.avg || {});
    } catch (error) {
      message.error(error?.response?.data?.error || "Error fetching report data:");
    }
  };

  const handleDateChange = (value, dateString) => {
    setSelectedDates(dateString);
    fetchReportData(dateString);
  };

  const handleDownload = () => {
    const cols = [
      { title: "Order Number", dataIndex: "order_number" },
      { title: "Test name", dataIndex: "test_name" },
      { title: "Order created", dataIndex: "order_created" },
      { title: "Customer Name", dataIndex: "customer_name" },
      { title: "Wellness Executive", dataIndex: "wellness_executive_name" },
      { title: "Order Accept", dataIndex: "order_accept" },
      { title: "Arriving at location", dataIndex: "arriving_at_location" },
      { title: "Order completion time", dataIndex: "order_completion" },
      { title: "Lab reaching time", dataIndex: "lab_reaching" },
      { title: "Report generate time", dataIndex: "report_generated" },
      { title: "Order created vs Accepting of order (in Min)", dataIndex: "order_create_vs_accept" },
      { title: "Order created vs Arriving at location (in Min)", dataIndex: "order_create_vs_arrive" },
      { title: "Order Accept vs Order completion time (in Min)", dataIndex: "order_accept_vs_completion" },
      { title: "Order completion time vs Lab reaching time (in Min)", dataIndex: "order_completion_vs_lab_reaching" },
      { title: "Order completion time vs Report generate time (in Min)", dataIndex: "order_completion_vs_report_generated" },
      { title: "Tat", dataIndex: "max_tat" },
    ];
    const csvContent = [
      cols.map((c) => c.title).join(","),
      ...data.map((row) =>
        cols.map((c) => {
          if (c.dataIndex === "order_created" && row[c.dataIndex])
            return moment(row[c.dataIndex]).format("DD/MM/YYYY hh:mm A");
          return row[c.dataIndex] || "";
        }).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "TatReport.csv";
    link.click();
  };

  const columns = [
    { title: "Order Number", dataIndex: "order_number", key: "order_number", width: 120, fixed: "left" },
    { title: "Test Name", dataIndex: "test_name", key: "test_name", width: 130 },
    { title: "Order Created", dataIndex: "order_created", key: "order_created", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Customer Name", dataIndex: "customer_name", key: "customer_name", width: 130, render: (text) => text || "-" },
    { title: "Wellness Executive", dataIndex: "wellness_executive_name", key: "wellness_executive_name", width: 145, render: (text) => text || "-" },
    { title: "Order Accept", dataIndex: "order_accept", key: "order_accept", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Arriving at Location", dataIndex: "arriving_at_location", key: "arriving_at_location", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Order Completion Time", dataIndex: "order_completion", key: "order_completion", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Lab Reaching Time", dataIndex: "lab_reaching", key: "lab_reaching", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Report Generate Time", dataIndex: "report_generated", key: "report_generated", width: 155, render: (text) => text ? moment(text).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Created vs Accepting (Min)", dataIndex: "order_create_vs_accept", key: "order_create_vs_accept", width: 160, render: (text) => text || "-" },
    { title: "Created vs Arriving (Min)", dataIndex: "order_create_vs_arrive", key: "order_create_vs_arrive", width: 155, render: (text) => text || "-" },
    { title: "Accept vs Completion (Min)", dataIndex: "order_accept_vs_completion", key: "order_accept_vs_completion", width: 165, render: (text) => text || "-" },
    { title: "Completion vs Lab (Min)", dataIndex: "order_completion_vs_lab_reaching", key: "order_completion_vs_lab_reaching", width: 155, render: (text) => text || "-" },
    { title: "Completion vs Report (Min)", dataIndex: "order_completion_vs_report_generated", key: "order_completion_vs_report_generated", width: 165, render: (text) => text || "-" },
    { title: "TAT", dataIndex: "max_tat", key: "max_tat", width: 80 },
  ];

  return (
    <div style={{ padding: "10px", margin: "10px" }}>
      {/* Header row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={6}>
          <div style={{ fontWeight: 600 }}>
            Total: <Tag>{data?.length}</Tag>
          </div>
        </Col>
        <Col xs={24} sm={18} style={{ display: "flex", justifyContent: "flex-end" }}>
          <RangePicker
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            style={{ width: "100%", maxWidth: 420 }}
            size="large"
            getPopupContainer={(trigger) => document.body}
            dropdownClassName="custom-range-picker-dropdown"
          />
        </Col>
      </Row>

      {/* Averages */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Tag color="blue" style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
          Avg Created vs Accept: {averages?.avg_order_create_vs_accept ? `${averages.avg_order_create_vs_accept.toFixed(1)} min` : "-"}
        </Tag>
        <Tag color="green" style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
          Avg Created vs Arrive: {averages?.avg_order_create_vs_arrive ? `${averages.avg_order_create_vs_arrive.toFixed(1)} min` : "-"}
        </Tag>
        <Tag color="purple" style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
          Avg Accept vs Completion: {averages?.avg_order_accept_vs_completion ? `${averages.avg_order_accept_vs_completion.toFixed(1)} min` : "-"}
        </Tag>
        <Tag color="orange" style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
          Avg Completion vs Lab: {averages?.avg_order_completion_vs_lab_reaching ? `${averages.avg_order_completion_vs_lab_reaching.toFixed(1)} min` : "-"}
        </Tag>
        <Tag color="red" style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, fontWeight: 600 }}>
          Avg Completion vs Report: {averages?.avg_order_completion_vs_report_generated ? `${averages.avg_order_completion_vs_report_generated.toFixed(1)} min` : "-"}
        </Tag>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record, index) => index}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={{ pageSize: 10, responsive: true }}
        />
      </div>

      <style>{`
        @media (max-width: 768px) {
          .custom-range-picker-dropdown .ant-picker-panels {
            flex-direction: column !important;
          }
          .custom-range-picker-dropdown {
            max-width: 95vw !important;
            overflow-x: auto !important;
          }
          .ant-table { font-size: 12px !important; }
          .ant-table-cell { padding: 6px 8px !important; white-space: nowrap; }
          .ant-tag { font-size: 11px !important; }
        }
      `}</style>
    </div>
  );
};

export default TatReport;